// Parser for the FTMS Rower Data characteristic (0x2AD1).
// Spec: Bluetooth SIG Fitness Machine Service v1.0, §4.10.
//
// Packet layout: 16-bit flags at offset 0, then a variable-length payload.
// Bit 0 is "More Data" with inverted semantics: when CLEAR, the packet
// includes Stroke Rate (uint8, ×0.5 spm) and Stroke Count (uint16).
//
// DEFENSIVE: Length-guarded on every field. Truncated payloads return null
// for any field that doesn't fit, rather than throwing.

import type { RowerMetrics } from "../types";

const FLAG_MORE_DATA = 0x0001;
const FLAG_AVG_STROKE_RATE = 0x0002;
const FLAG_TOTAL_DISTANCE = 0x0004;
const FLAG_INST_PACE = 0x0008;
const FLAG_AVG_PACE = 0x0010;
const FLAG_INST_POWER = 0x0020;
const FLAG_AVG_POWER = 0x0040;
const FLAG_RESISTANCE = 0x0080;
const FLAG_EXPENDED_ENERGY = 0x0100;
const FLAG_HEART_RATE = 0x0200;
const FLAG_METABOLIC_EQ = 0x0400;
const FLAG_ELAPSED_TIME = 0x0800;
const FLAG_REMAINING_TIME = 0x1000;

const fits = (view: DataView, offset: number, size: number): boolean =>
  offset + size <= view.byteLength;

export function parseRowerData(view: DataView): RowerMetrics {
  const out: RowerMetrics = {
    type: "rower",
    power_watts: null,
    heart_rate_bpm: null,
    elapsed_time_s: null,
    remaining_time_s: null,
    stroke_rate_spm: null,
    stroke_count: null,
    avg_stroke_rate_spm: null,
    pace_sec_per_500m: null,
    avg_pace_sec_per_500m: null,
    avg_power_watts: null,
    total_distance_m: null,
    resistance_level: null,
    total_energy_kcal: null,
  };

  if (view.byteLength < 2) return out;
  const flags = view.getUint16(0, true);
  let o = 2;

  if ((flags & FLAG_MORE_DATA) === 0) {
    if (fits(view, o, 1)) {
      out.stroke_rate_spm = view.getUint8(o) * 0.5;
      o += 1;
    }
    if (fits(view, o, 2)) {
      out.stroke_count = view.getUint16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_AVG_STROKE_RATE) {
    if (fits(view, o, 1)) {
      out.avg_stroke_rate_spm = view.getUint8(o) * 0.5;
      o += 1;
    }
  }
  if (flags & FLAG_TOTAL_DISTANCE) {
    if (fits(view, o, 3)) {
      const lo = view.getUint16(o, true);
      const hi = view.getUint8(o + 2);
      out.total_distance_m = lo | (hi << 16);
      o += 3;
    }
  }
  if (flags & FLAG_INST_PACE) {
    if (fits(view, o, 2)) {
      out.pace_sec_per_500m = view.getUint16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_AVG_PACE) {
    if (fits(view, o, 2)) {
      out.avg_pace_sec_per_500m = view.getUint16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_INST_POWER) {
    if (fits(view, o, 2)) {
      out.power_watts = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_AVG_POWER) {
    if (fits(view, o, 2)) {
      out.avg_power_watts = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_RESISTANCE) {
    if (fits(view, o, 2)) {
      out.resistance_level = view.getInt16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_EXPENDED_ENERGY) {
    // Total (uint16 kcal), per hour (uint16), per minute (uint8); 0xFFFF is
    // "not available".
    if (fits(view, o, 5)) {
      const total = view.getUint16(o, true);
      if (total !== 0xffff) out.total_energy_kcal = total;
      o += 5;
    }
  }
  if (flags & FLAG_HEART_RATE) {
    if (fits(view, o, 1)) {
      out.heart_rate_bpm = view.getUint8(o);
      o += 1;
    }
  }
  if (flags & FLAG_METABOLIC_EQ) {
    if (fits(view, o, 1)) {
      o += 1;
    }
  }
  if (flags & FLAG_ELAPSED_TIME) {
    if (fits(view, o, 2)) {
      out.elapsed_time_s = view.getUint16(o, true);
      o += 2;
    }
  }
  if (flags & FLAG_REMAINING_TIME) {
    if (fits(view, o, 2)) {
      out.remaining_time_s = view.getUint16(o, true);
    }
  }

  return out;
}
